import { Icon } from 'ui';
import CardGeneric from 'core/modules/components/cards/components/CardGeneric';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChatbotContext } from '../../hooks/ChatbotContext';
import LemonadeActivity from './LemonadeActivity';
import PracticeExerciseControls from './PracticeExerciseControls';

const ChatbotPracticeExercise = ({
  exercise,
  isChatAvailable,
  activityInstanceId,
  onActivityComplete,
  activityExtraInfo,
  isReportingError,
  onReportError,
  lessonTitle,
  sessionData,
}) => {
  const { t } = useTranslation(['chatbot']);
  const { openChatbot, queueMessage, setChatbotMode } = useChatbotContext();

  const questionRef = useRef(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const messagesOnWrongAnswer = useMemo(
    function () {
      return t('practice_wrong_answer_messages', { returnObjects: true });
    },
    [t]
  );

  const onQueueWrongAnswer = useCallback(
    function (response) {
      setChatbotMode('practice');
      openChatbot();
      const randomMessage = messagesOnWrongAnswer[Math.floor(Math.random() * messagesOnWrongAnswer.length)];
      queueMessage({
        message: randomMessage,
        options: response
          ? {
              studentResponse: JSON.stringify(response),
              activityInstanceId, // Tag message with the current activity to prevent cross-activity leakage
              automaticMessage: true,
            }
          : { activityInstanceId, automaticMessage: true },
      });
    },
    [messagesOnWrongAnswer, openChatbot, queueMessage, setChatbotMode, activityInstanceId]
  );

  const onQueueIDontKnow = useCallback(
    function () {
      setChatbotMode('practice');
      openChatbot();
      queueMessage({
        message: t('practice_i_dont_know_message'),
      });
    },
    [openChatbot, queueMessage, setChatbotMode, t]
  );

  const handleInteraction = useCallback(function (changed) {
    setHasUserInteracted(Boolean(changed));
  }, []);

  const exerciseId = exercise?.id || exercise?.data?.id;

  useEffect(
    function () {
      setHasUserInteracted(false);
    },
    [exerciseId]
  );

  return (
    <div className="ai-practice-view__activity-container">
      {(window.location.href.includes('localhost') || window.location.href.includes('tenant-ai-1.oneclicklabs.es')) && (
        <div className="ai-practice-view__activity-info">
          <div className="ai-practice-view__activity-info-item">
            <strong>Fase de Actividad:</strong> {activityExtraInfo?.activityPhase || 'N/A'}
          </div>
          <div className="ai-practice-view__activity-info-item">
            <strong>Componente de Conocimiento:</strong> {activityExtraInfo?.associatedKcId || 'N/A'}
          </div>
          <div className="ai-practice-view__activity-info-item">
            <strong>Referencia KC:</strong> {activityExtraInfo?.associatedKcReferenceId || 'N/A'}
          </div>
        </div>
      )}
      <CardGeneric>
        <div className="ai-practice-view__lesson-title">
          <div className="ai-practice-view__lesson-title-text" dangerouslySetInnerHTML={{ __html: lessonTitle }} />
          <div className="ai-practice-view__notify">
            <button className="ai-practice-view__notify-button" onClick={onReportError} disabled={isReportingError}>
              <Icon name="flag" size="small" />
            </button>
          </div>
        </div>
        <div className="ai-practice-view__exercise-content">
          <LemonadeActivity key={exerciseId} ref={questionRef} exercise={exercise} onInteraction={handleInteraction} />
          <PracticeExerciseControls
            questionRef={questionRef}
            hasUserInteracted={hasUserInteracted}
            activityInstanceId={activityInstanceId}
            isChatAvailable={isChatAvailable}
            onActivityComplete={onActivityComplete}
            onQueueIDontKnow={onQueueIDontKnow}
            onQueueWrongAnswer={onQueueWrongAnswer}
            activityExtraInfo={activityExtraInfo}
            sessionData={sessionData}
          />
        </div>
      </CardGeneric>
    </div>
  );
};

ChatbotPracticeExercise.propTypes = {
  exercise: PropTypes.object.isRequired,
  activityInstanceId: PropTypes.string,
  isChatAvailable: PropTypes.bool,
  onActivityComplete: PropTypes.func,
  activityExtraInfo: PropTypes.object, // ← TODO: Remove after testing
  isReportingError: PropTypes.bool,
  onReportError: PropTypes.func.isRequired,
  lessonTitle: PropTypes.string,
  sessionData: PropTypes.object,
};

export default ChatbotPracticeExercise;
